import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import ReportsTab from '../components/admin/ReportsTab';
import TakedownsTab from '../components/admin/TakedownsTab';
import RoleManagement from '../components/RoleManagement';
import UsersTab from '../components/admin/UsersTab';
import PostsTab from '../components/admin/PostsTab';
import CouncillorsTab from '../components/admin/CouncillorsTab';
import VotesTab from '../components/admin/VotesTab';

// ── Bulk JSON vote import component ──────────────────────────────────────────
const BulkVoteImport = ({ onImport }) => {
  const [open, setOpen] = React.useState(false);
  const [json, setJson] = React.useState('');
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState('');

  const parse = () => {
    setError('');
    try {
      const parsed = JSON.parse(json);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      setPreview(arr);
    } catch (e) {
      setError(`Invalid JSON: ${e.message}`);
    }
  };

  const doImport = async () => {
    if (!preview) return;
    await onImport(preview);
    setOpen(false);
    setJson('');
    setPreview(null);
  };

  if (!open) return (
    <div className="mt-4">
      <button onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-gray-300 font-mono text-xs rounded transition-colors border border-zinc-700">
        ↑ BULK JSON IMPORT
      </button>
    </div>
  );

  return (
    <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded p-5 mt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-mono text-sm text-white uppercase tracking-wider">Bulk Vote Import (JSON)</h3>
        <button onClick={() => { setOpen(false); setJson(''); setPreview(null); setError(''); }}
          className="text-gray-500 hover:text-white text-xs font-mono">✕ Cancel</button>
      </div>
      <p className="font-mono text-[10px] text-gray-500">
        Paste a JSON array. Supported fields: <span className="text-[#C5A52A]">date, motion, desc, result, votes{'{'} YES, NO, ABSTAINED {'}'}</span>
      </p>
      <textarea
        value={json}
        onChange={e => { setJson(e.target.value); setPreview(null); setError(''); }}
        className="w-full h-48 bg-black border border-[#2a2a2a] text-green-400 font-mono text-xs px-3 py-2 rounded resize-none"
        placeholder={'[\n  {\n    "date": "Dec 19, 2025",\n    "motion": "BYLAW 2025-55",\n    "desc": "Authorization to divert $20M...",\n    "result": "CARRIED [6-5]",\n    "votes": { "YES": ["Name 1"], "NO": ["Name 2"] }\n  }\n]'}
        spellCheck={false}
      />
      {error && <p className="text-red-400 font-mono text-xs">{error}</p>}
      {preview && (
        <div className="bg-[#111] border border-green-900/40 rounded p-3">
          <p className="font-mono text-xs text-green-400 mb-1">✓ Valid JSON — {preview.length} vote{preview.length !== 1 ? 's' : ''} ready to import</p>
          {preview.slice(0, 3).map((v, i) => (
            <p key={i} className="font-mono text-[10px] text-gray-400 truncate">{i + 1}. {v.motion} · {v.date} · {v.result}</p>
          ))}
          {preview.length > 3 && <p className="font-mono text-[10px] text-gray-600">…and {preview.length - 3} more</p>}
        </div>
      )}
      <div className="flex gap-3">
        {!preview
          ? <button onClick={parse} className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white font-mono text-xs rounded transition-colors">PARSE & PREVIEW</button>
          : <button onClick={doImport} className="px-4 py-2 bg-[#C5A52A] hover:bg-yellow-400 text-black font-mono text-xs font-bold rounded transition-colors">IMPORT {preview.length} VOTES</button>
        }
      </div>
    </div>
  );
};
import KeywordsTab from '../components/admin/KeywordsTab';
import StoreTab from '../components/admin/StoreTab';
import WardsTab from '../components/admin/WardsTab';
import FoundersCornerTab from '../components/admin/FoundersCornerTab';
import RealtorAdsTab from '../components/admin/RealtorAdsTab';
import OverviewTab from '../components/admin/OverviewTab';
import WardPulseAdmin from '../components/admin/WardPulseAdmin';
import AuditorTab from '../components/admin/AuditorTab';
import AlertsTab from '../components/admin/AlertsTab';
import PushTab from '../components/admin/PushTab';
import EventsTab from '../components/admin/EventsTab';
import NonprofitAdminTab from '../components/admin/NonprofitAdminTab';
import WardPhotosAdminTab from '../components/admin/WardPhotosAdminTab';
import WardReportModal from '../components/admin/WardReportModal';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const location = useLocation();
  // Read ?tab= from URL on load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) setActiveTab(tab);
  }, [location.search]);
  // Expose setActiveTab globally so OverviewTab quick actions work
  useEffect(() => { window.__adminSetTab = setActiveTab; return () => { delete window.__adminSetTab; }; }, []);
  const [loading, setLoading] = useState(true);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Posts
  const [posts, setPosts] = useState([]);
  const [postCategoryFilter, setPostCategoryFilter] = useState('all');
  
  // Users
  const [users, setUsers] = useState([]);
  
  // Messages
  const [messages, setMessages] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  // Ads
  const [ads, setAds] = useState([]);
  const [realtorAds, setRealtorAds] = useState([]);
  const [showAdModal, setShowAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adForm, setAdForm] = useState({ 
    title: '', content: '', link: '', image_url: '', 
    ad_type: 'standard', is_affiliate: false 
  });
  
  // Badges
  const [badges, setBadges] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  
  // Councillors
  const [councillors, setCouncillors] = useState([]);
  const [councillorClaimsStatus, setCouncillorClaimsStatus] = useState([]);
  const [pendingBlasts, setPendingBlasts] = useState([]);
  const [errorReports, setErrorReports] = useState([]);
  const [sendingClaimLink, setSendingClaimLink] = useState(null);
  const [showCouncillorModal, setShowCouncillorModal] = useState(false);
  const [councillorForm, setCouncillorForm] = useState({
    id: '', name: '', match_name: '', ward: '', email: '', phone: '', bio: '', image_url: '', postal_codes: '',
    emails_delivered: 0, emails_responded: 0, emails_redemption: 0
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Votes
  const [votes, setVotes] = useState([]);
  const [pendingVotes, setPendingVotes] = useState([]);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [voteForm, setVoteForm] = useState({
    date: '', motion: '', result: '', yes_votes: [], no_votes: [], 
    abstained_votes: [], not_present_votes: [], conflict_votes: [], pdf_link: '', description: ''
  });
  const [deleteConfirmVote, setDeleteConfirmVote] = useState(null);
  const [confirmDeleteBadge, setConfirmDeleteBadge] = useState(null); // badge object pending delete confirm
  const [showWardReport, setShowWardReport] = useState(false);
  const [showDeletedBadges, setShowDeletedBadges] = useState(false);

  // Badge Award / Removal tool
  const [badgeMgrMode, setBadgeMgrMode] = useState('award'); // 'award' | 'remove'
  const [badgeMgrQuery, setBadgeMgrQuery] = useState('');
  const [badgeMgrResults, setBadgeMgrResults] = useState([]);
  const [badgeMgrUser, setBadgeMgrUser] = useState(null);
  const [badgeMgrUserBadges, setBadgeMgrUserBadges] = useState([]);
  const [badgeMgrSelectedCode, setBadgeMgrSelectedCode] = useState('');
  const [badgeMgrLoading, setBadgeMgrLoading] = useState(false);
  
  // Keywords
  const [keywords, setKeywords] = useState([]);
  
  // Issue Categories
  const [issueCategories, setIssueCategories] = useState([]);
  const [showIssueCategoryModal, setShowIssueCategoryModal] = useState(false);
  const [issueCategoryForm, setIssueCategoryForm] = useState({
    id: '', group_name: '', name: '', verification_distance_meters: 100,
    requires_checkin: true, icon: '', points_report: 5, points_verify: 3
  });
  const [wardStats, setWardStats] = useState(null);
  const [newKeyword, setNewKeyword] = useState({ word: '', action: 'block' });

  // Community Leaders
  const [communityLeaders, setCommunityLeaders] = useState([]);
  const [showLeaderModal, setShowLeaderModal] = useState(false);
  const [leaderWard, setLeaderWard] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');

  // Takedown Requests
  const [takedowns, setTakedowns] = useState([]);
  
  // Post Reports
  const [reports, setReports] = useState([]);

  useEffect(() => {
    if (!user || !isAdmin()) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchData();
  }, [user, isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [postsRes, usersRes, messagesRes, adsRes, realtorAdsRes, councillorsRes, votesRes, keywordsRes, leadersRes, takedownsRes, reportsRes, badgesRes, hotspotsRes, issueCatsRes, wardStatsRes, claimsRes, blastsRes, errorRepsRes, pendingVotesRes] = await Promise.all([
        api.get('/admin/posts'),
        api.get('/admin/users'),
        api.get('/admin/messages'),
        api.get('/admin/ads'),
        api.get('/admin/ward-realtor-ads').catch(() => ({ data: [] })),
        api.get('/councillors'),
        api.get('/votes'),
        api.get('/admin/keywords'),
        api.get('/admin/community-leaders'),
        api.get('/admin/takedowns').catch(() => ({ data: [] })),
        api.get('/admin/reports').catch(() => ({ data: [] })),
        api.get('/admin/badges').catch(() => ({ data: { badges: [] } })),
        api.get('/admin/analytics/hotspots').catch(() => ({ data: { hotspots: [] } })),
        api.get('/issue-categories?active_only=false').catch(() => ({ data: { categories: [] } })),
        api.get('/admin/ward-stats').catch(() => ({ data: null })),
        api.get('/admin/councillors/claims-status').catch(() => ({ data: [] })),
        api.get('/admin/email-blasts/pending').catch(() => ({ data: [] })),
        api.get('/admin/councillor-error-reports').catch(() => ({ data: [] })),
        api.get('/admin/votes/pending').catch(() => ({ data: { votes: [] } })),
      ]);
      
      setPosts(postsRes.data);
      setUsers(usersRes.data.users);
      setMessages(messagesRes.data);
      setAds(adsRes.data);
      setRealtorAds(Array.isArray(realtorAdsRes.data) ? realtorAdsRes.data : []);
      setCouncillors(councillorsRes.data);
      setVotes(votesRes.data);
      setKeywords(keywordsRes.data);
      setCommunityLeaders(leadersRes.data?.community_leaders || []);
      setTakedowns(takedownsRes.data || []);
      setReports(reportsRes.data || []);
      setBadges(badgesRes.data?.badges || []);
      setHotspots(hotspotsRes.data?.hotspots || []);
      setIssueCategories(issueCatsRes.data?.categories || []);
      setWardStats(wardStatsRes.data);
      setPendingVotes(pendingVotesRes.data?.votes || []);
      setCouncillorClaimsStatus(claimsRes.data || []);
      setPendingBlasts(blastsRes.data || []);
      setErrorReports(errorRepsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Post Moderation
  const fetchBadges = fetchData; // alias - badges are refreshed via fetchData
  const moderatePost = async (postId, action, reason = null) => {
    try {
      await api.put(`/admin/posts/${postId}/moderate?action=${action}${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`);
      toast.success(`Post ${action}ed successfully`);
      fetchData();
    } catch (error) {
      toast.error('Failed to moderate post');
    }
  };

  // Message Reply
  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyingTo || !replyText.trim()) return;

    try {
      const formData = new FormData();
      formData.append('reply', replyText);
      
      await api.post(`/admin/messages/${replyingTo}/reply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Reply sent successfully');
      setReplyingTo(null);
      setReplyText('');
      fetchData();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  // Ad Management
  const saveAd = async (e) => {
    e.preventDefault();
    try {
      if (editingAd) {
        await api.put(`/admin/ads/${editingAd}`, adForm);
        toast.success('Ad updated successfully');
      } else {
        await api.post('/admin/ads', adForm);
        toast.success('Ad created successfully');
      }
      setShowAdModal(false);
      setEditingAd(null);
      setAdForm({ title: '', content: '', link: '', image_url: '', ad_type: 'standard', is_affiliate: false });
      fetchData();
    } catch (error) {
      toast.error('Failed to save ad');
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await api.delete(`/admin/ads/${adId}`);
      toast.success('Ad deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const activatePendingAd = async (ad) => {
    try {
      await api.put(`/admin/ads/${ad.id}`, { ...ad, is_active: true, pending_review: false });
      toast.success(`"${ad.title}" is now live!`);
      fetchData();
    } catch {
      toast.error('Failed to activate ad');
    }
  };

  // Councillor Claim Link
  const sendClaimLink = async (councillorId) => {
    setSendingClaimLink(councillorId);
    try {
      const res = await api.post(`/admin/councillors/send-claim-link/${councillorId}`);
      toast.success(res.data.message || 'Claim link sent!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send claim link');
    } finally {
      setSendingClaimLink(null);
    }
  };

  // Email Blast Review
  const reviewBlast = async (blastId, status, notes = '') => {
    try {
      await api.put(`/admin/email-blast/${blastId}/review`, { status, admin_review_notes: notes });
      toast.success(`Email blast ${status === 'approved' ? 'approved' : 'rejected'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to review email blast');
    }
  };

  // Councillor Management
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/admin/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCouncillorForm({ ...councillorForm, image_url: response.data.image_url });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveCouncillor = async (e) => {
    e.preventDefault();
    try {
      const existing = councillors.find(c => c.id === councillorForm.id);
      
      // Only send the basic profile fields, NOT the metrics
      const profileData = {
        id: councillorForm.id,
        name: councillorForm.name,
        match_name: councillorForm.match_name,
        ward: councillorForm.ward,
        email: councillorForm.email,
        phone: councillorForm.phone,
        bio: councillorForm.bio,
        image_url: councillorForm.image_url,
        postal_codes: councillorForm.postal_codes
      };
      
      if (existing) {
        await api.put(`/admin/councillors/${councillorForm.id}`, profileData);
        toast.success('Councillor updated');
      } else {
        await api.post('/admin/councillors', profileData);
        toast.success('Councillor added');
      }
      setShowCouncillorModal(false);
      setCouncillorForm({ id: '', name: '', match_name: '', ward: '', email: '', phone: '', bio: '', image_url: '', postal_codes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save councillor');
    }
  };

  // Vote Management
  const toggleVote = (councillorName, voteType) => {
    // Remove from all other vote types first
    const newForm = {
      yes_votes: voteForm.yes_votes.filter(v => v !== councillorName),
      no_votes: voteForm.no_votes.filter(v => v !== councillorName),
      abstained_votes: voteForm.abstained_votes.filter(v => v !== councillorName),
      not_present_votes: voteForm.not_present_votes.filter(v => v !== councillorName),
      conflict_votes: voteForm.conflict_votes.filter(v => v !== councillorName)
    };
    
    // Add to selected type (toggle - if already there, remove it)
    const currentList = voteForm[`${voteType}_votes`];
    if (currentList.includes(councillorName)) {
      // Already selected, so we're removing it (already removed above)
    } else {
      // Add to new list
      newForm[`${voteType}_votes`].push(councillorName);
    }
    
    setVoteForm({
      ...voteForm,
      ...newForm
    });
  };

  const saveVote = async (e) => {
    e.preventDefault();
    try {
      const voteData = {
        date: voteForm.date,
        motion: voteForm.motion,
        description: voteForm.description || null,
        result: voteForm.result,
        yes_votes: voteForm.yes_votes,
        no_votes: voteForm.no_votes,
        abstained_votes: voteForm.abstained_votes,
        not_present_votes: voteForm.not_present_votes,
        conflict_votes: voteForm.conflict_votes,
        pdf_link: voteForm.pdf_link
      };
      
      await api.post('/admin/votes', voteData);
      toast.success('Vote added');
      setShowVoteModal(false);
      setVoteForm({ 
        date: '', motion: '', result: '', yes_votes: [], no_votes: [], 
        abstained_votes: [], not_present_votes: [], conflict_votes: [], pdf_link: '' 
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to save vote');
    }
  };

  // User Management
  const banUser = async (userId, ban) => {
    try {
      await api.put(`/admin/users/${userId}/ban?ban=${ban}`);
      toast.success(ban ? 'User banned' : 'User unbanned');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const updateUserRole = async (userId, newRole, ward = null, action = null) => {
    // Handle verify action
    if (action === 'verify') {
      try {
        await api.put(`/admin/users/${userId}/verify`);
        toast.success('User verified — they can now log in');
        fetchData();
      } catch (error) {
        toast.error('Failed to verify user');
      }
      return;
    }
    try {
      const params = { role: newRole };
      if (ward) {
        params.ward = ward;
      }
      await api.put(`/admin/users/${userId}/role`, null, { params });
      toast.success(`User role updated to ${newRole}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure? This will delete all user data including posts and messages.')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message? This will prevent automated reminders.')) return;
    try {
      await api.delete(`/admin/messages/${messageId}`);
      toast.success('Message deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const deleteVote = async (voteId) => {
    try {
      await api.delete(`/admin/votes/${voteId}`);
      toast.success('Vote deleted');
      setDeleteConfirmVote(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete vote');
    }
  };

  // Keyword Management
  const addKeyword = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/keywords', newKeyword);
      toast.success('Keyword added');
      setNewKeyword({ word: '', action: 'block' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add keyword');
    }
  };

  const deleteKeyword = async (keywordId) => {
    try {
      await api.delete(`/admin/keywords/${keywordId}`);
      toast.success('Keyword deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete keyword');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mx-auto"></div>
          <p className="text-gray-500 mt-4 font-mono text-sm">Loading admin panel...</p>
        </div>
      </div>
    );
  }



  // Community Leader Management
  const handleAssignLeader = async (userId, ward) => {
    try {
      await api.post('/admin/community-leaders/assign', { user_id: userId, ward });
      toast.success('Community leader assigned!');
      setShowLeaderModal(false);
      setSelectedUserId('');
      setLeaderWard('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign leader');
    }
  };

  const handleRemoveLeader = async (userId) => {
    if (!window.confirm('Remove community leader role?')) return;
    try {
      await api.delete(`/admin/community-leaders/${userId}`);
      toast.success('Community leader removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove leader');
    }
  };

  // Takedown Management
  const updateTakedownStatus = async (takedownId, newStatus, adminNotes = '') => {
    try {
      const params = new URLSearchParams({ status: newStatus });
      if (adminNotes) params.append('admin_notes', adminNotes);
      await api.put(`/admin/takedowns/${takedownId}?${params.toString()}`);
      toast.success(`Takedown request ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update takedown status');
    }
  };

  // Report Management
  const handleReportAction = async (reportId, action, adminNotes = '') => {
    try {
      const params = new URLSearchParams({ action });
      if (adminNotes) params.append('admin_notes', adminNotes);
      await api.put(`/admin/reports/${reportId}?${params.toString()}`);
      toast.success(`Report ${action === 'dismiss' ? 'dismissed' : action === 'moderate_post' ? 'post moderated' : 'post deleted'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to handle report');
    }
  };


  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <div className="text-green-500 text-2xl">⚙️</div>
              <div>
                <h1 className="text-xl md:text-2xl font-black text-white tracking-tight">
                  ADMIN PANEL
                </h1>
                <p className="text-gray-500 text-xs font-mono uppercase tracking-wider">
                  Platform Management
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-400 hover:text-white text-sm font-mono uppercase tracking-wider transition-colors">
                Home
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {['overview', 'posts', 'users', 'badges', 'roles', 'store', 'wards & councillors', 'founders', 'issues', 'messages', 'ads', 'votes', 'keywords', 'leaders', 'takedowns', 'reports', 'events', 'nonprofits', 'ward photos', 'email designer', 'ward pulse', 'council auditor', 'alerts', 'push'].map((tab) => {
              const pendingTakedowns = takedowns.filter(t => t.status === 'pending').length;
              const pendingReports = reports.filter(r => r.status === 'pending').length;
              const pendingAds = [
                ...ads.filter(a => a.pending_review && !a.is_active),
                ...(Array.isArray(realtorAds) ? realtorAds.filter(a => a.pending_review && !a.is_active) : []),
              ].length;
              const pendingVotesCount = pendingVotes.length;
              const showBadge = (tab === 'takedowns' && pendingTakedowns > 0) || (tab === 'reports' && pendingReports > 0) || (tab === 'ads' && pendingAds > 0) || (tab === 'votes' && pendingVotesCount > 0) || (tab === 'ward photos');
              const badgeCount = tab === 'takedowns' ? pendingTakedowns : tab === 'reports' ? pendingReports : tab === 'votes' ? pendingVotesCount : pendingAds;
              const badgeColor = tab === 'ads' ? 'bg-yellow-500' : tab === 'votes' ? 'bg-yellow-400' : 'bg-red-600';
              const activeColor = tab === 'ads' && pendingAds > 0 && activeTab === tab
                ? 'bg-yellow-500 text-black font-bold'
                : (tab === 'takedowns' && pendingTakedowns > 0) || (tab === 'reports' && pendingReports > 0) && activeTab === tab
                  ? 'bg-red-500 text-white font-bold'
                  : activeTab === tab ? 'bg-green-500 text-black font-bold' : null;
              
              return (
                <button
                  key={tab}
                  onClick={() => {
                    if (tab === 'email designer') {
                      navigate('/admin/email-designer');
                    } else {
                      setActiveTab(tab);
                    }
                  }}
                  className={
                    'px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-sm transition-colors whitespace-nowrap relative ' +
                    (tab === 'email designer'
                      ? 'bg-gray-800 text-[#C5A52A] hover:text-white border border-[#C5A52A]/30'
                      : activeColor
                        ? activeColor
                        : activeTab === tab
                          ? 'bg-green-500 text-black font-bold'
                          : 'bg-gray-800 text-gray-400 hover:text-white')
                  }
                >
                  {tab}
                  {showBadge && (
                    <span className={`absolute -top-1 -right-1 ${badgeColor} text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold`}>
                      {badgeCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <OverviewTab
            posts={posts}
            users={users}
            messages={messages}
            ads={ads}
            reports={reports}
            takedowns={takedowns}
            realtorAds={realtorAds}
          />
        )}

        {/* POSTS TAB */}
        {activeTab === 'posts' && (
          <PostsTab 
            posts={posts}
            postCategoryFilter={postCategoryFilter}
            setPostCategoryFilter={setPostCategoryFilter}
            moderatePost={moderatePost}
            fetchData={fetchData}
          />
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Message Monitor</h2>
            <div className="space-y-4">
              {messages.map((msg) => (
                <div key={msg.id} className="bg-gray-900 border border-gray-800 p-6 rounded-sm">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-white font-bold">
                        {msg.user_name} → {msg.councillor_name}
                      </h3>
                      <p className="text-gray-500 text-xs font-mono">
                        ID: {msg.anonymous_id} • {new Date(msg.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-mono px-2 py-1 rounded ${
                        msg.status === 'replied' ? 'bg-green-900 text-green-500' : 'bg-yellow-900 text-yellow-500'
                      }`}>
                        {msg.status}
                      </span>
                      <button
                        onClick={() => deleteMessage(msg.id)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                        title="Delete message (prevents reminders)"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-gray-300 mb-4">{msg.message}</p>
                  
                  {msg.reply ? (
                    <div className="bg-gray-950 border-l-4 border-green-500 p-4">
                      <p className="text-sm text-green-500 mb-2 uppercase font-mono">Reply:</p>
                      <p className="text-white">{msg.reply}</p>
                    </div>
                  ) : (
                    <div>
                      {replyingTo === msg.id ? (
                        <form onSubmit={submitReply} className="space-y-2">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm resize-none"
                            rows="4"
                            placeholder="Enter reply..."
                            required
                          />
                          <div className="flex gap-2">
                            <button
                              type="submit"
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase"
                            >
                              Send Reply
                            </button>
                            <button
                              type="button"
                              onClick={() => { setReplyingTo(null); setReplyText(''); }}
                              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <button
                          onClick={() => setReplyingTo(msg.id)}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-sm text-sm font-mono uppercase"
                        >
                          Add Reply
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <UsersTab 
            users={users}
            updateUserRole={updateUserRole}
            banUser={banUser}
            deleteUser={deleteUser}
          />
        )}

        {/* ADS TAB */}
        {activeTab === 'ads' && (
          <div className="space-y-10">

            {/* Pending Standard Ads Review */}
            {ads.filter(a => a.pending_review && !a.is_active).length > 0 && (
              <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-sm p-4">
                <div className="flex items-start gap-3">
                  <span className="text-yellow-400 text-xl mt-0.5">⏳</span>
                  <div className="flex-1">
                    <p className="text-yellow-400 font-bold mb-1">
                      {ads.filter(a => a.pending_review && !a.is_active).length} Standard Ad{ads.filter(a => a.pending_review && !a.is_active).length > 1 ? 's' : ''} Awaiting Review
                    </p>
                    <p className="text-gray-500 text-xs mb-3">Submitted by advertisers via self-serve checkout. Review and activate when ready.</p>
                    <div className="space-y-2">
                      {ads.filter(a => a.pending_review && !a.is_active).map(ad => (
                        <div key={ad.id} className="flex items-center justify-between bg-yellow-950/30 border border-yellow-900/40 px-4 py-2 rounded-sm gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {ad.image_url && (
                              <img src={ad.image_url} alt={ad.title} className="w-10 h-10 object-cover rounded flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <span className="text-white font-bold text-sm block truncate">{ad.title}</span>
                              <span className="text-gray-500 text-xs truncate block">{ad.content}</span>
                              {ad.advertiser_name && <span className="text-gray-600 text-xs">by {ad.advertiser_name}</span>}
                              {ad.email && <span className="text-gray-600 text-xs ml-2">· {ad.email}</span>}
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            {ad.link && (
                              <a href={ad.link} target="_blank" rel="noopener noreferrer"
                                className="text-xs font-mono uppercase tracking-wider border border-zinc-700 text-gray-400 hover:text-white px-3 py-1.5 transition-colors">
                                Preview
                              </a>
                            )}
                            <button
                              onClick={() => activatePendingAd(ad)}
                              className="text-xs font-mono uppercase tracking-wider bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 transition-colors"
                            >
                              ✓ Activate
                            </button>
                            <button
                              onClick={() => deleteAd(ad.id)}
                              className="text-xs font-mono uppercase tracking-wider bg-red-900/60 hover:bg-red-700 text-red-400 hover:text-white px-3 py-1.5 transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 1: Advertising Spots */}
            <div>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">Advertising Spots</h2>
                  <p className="text-gray-400 text-sm">
                    <span className="text-yellow-400 font-bold">★ Main Site Sponsor</span> — 1 active slot (top of homepage) &nbsp;|&nbsp; <span className="text-gray-300">Standard</span> — feed rotation
                  </p>
                </div>
                <button
                  onClick={() => { setShowAdModal(true); setEditingAd(null); }}
                  className="bg-green-500 hover:bg-green-600 text-black font-bold py-2 px-6 rounded-sm uppercase tracking-wider"
                >
                  + Create Ad
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ads.map((ad) => {
                  const isMainSponsor = ad.ad_type === 'main_sponsor';
                  const dateAdded = ad.created_at ? new Date(ad.created_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
                  return (
                    <div
                      key={ad.id}
                      className={`border p-5 rounded-sm relative ${
                        isMainSponsor
                          ? 'bg-[#1a1500] border-[#C5A52A] shadow-[0_0_12px_rgba(197,165,42,0.15)]'
                          : 'bg-gray-900 border-gray-800'
                      }`}
                    >
                      {isMainSponsor && (
                        <div className="absolute top-0 right-0 bg-[#C5A52A] text-black font-mono font-bold text-[10px] px-3 py-1 tracking-wider">
                          ★ SITE SPONSOR
                        </div>
                      )}
                      <div className="flex items-start gap-3 mb-3">
                        {ad.image_url && (
                          <img src={ad.image_url} alt={ad.title} className="w-14 h-14 object-cover rounded flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-bold text-base truncate ${isMainSponsor ? 'text-[#C5A52A]' : 'text-white'}`}>{ad.title}</h3>
                          <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{ad.content}</p>
                        </div>
                      </div>
                      {ad.link && (
                        <a href={ad.link} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline block mb-3 truncate">
                          {ad.link}
                        </a>
                      )}
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${
                            ad.is_active
                              ? 'bg-green-900/30 border-green-700/40 text-green-400'
                              : 'bg-gray-800 border-gray-700 text-gray-500'
                          }`}>
                            {ad.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                          <span className="text-[10px] font-mono text-gray-600">Added {dateAdded}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setEditingAd(ad.id); setAdForm(ad); setShowAdModal(true); }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-sm text-xs font-mono uppercase"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteAd(ad.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-sm text-xs font-mono uppercase"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Section 2: Ward Realtor Partners */}
            <div className="border-t border-[#1a1a1a] pt-8">
              <h2 className="text-2xl font-bold text-white mb-1">Ward Realtor Partners</h2>
              <p className="text-gray-400 text-sm mb-6">One exclusive realtor ad per ward. Shown on ward pages and user dashboards.</p>
              <RealtorAdsTab />
            </div>
          </div>
        )}

        {/* COUNCILLORS TAB */}
        {activeTab === 'wards & councillors' && (
          <div className="space-y-10">
            {showWardReport && <WardReportModal onClose={() => setShowWardReport(false)} />}
            {/* Section 1: Wards */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Wards</h2>
                <button
                  onClick={() => setShowWardReport(true)}
                  data-testid="generate-ward-report-trigger"
                  className="flex items-center gap-2 px-4 py-2 bg-[#C5A52A] text-black font-bold rounded text-sm hover:bg-yellow-400"
                >
                  Generate Ward Report
                </button>
              </div>
              <WardsTab />
            </div>
            {/* Section 2: Councillors */}
            <div className="border-t border-[#1a1a1a] pt-8">
              <h2 className="text-2xl font-bold text-white mb-6">Councillors</h2>
              <CouncillorsTab
                councillorClaimsStatus={councillorClaimsStatus}
                pendingBlasts={pendingBlasts}
                errorReports={errorReports}
                sendingClaimLink={sendingClaimLink}
                sendClaimLink={sendClaimLink}
                setCouncillorForm={setCouncillorForm}
                setShowCouncillorModal={setShowCouncillorModal}
                reviewBlast={reviewBlast}
                onImitateCouncillor={(id) => navigate(`/admin/councillor/${id}`)}
              />
            </div>
          </div>
        )}

        {/* VOTES TAB */}
        {activeTab === 'votes' && (
          <div className="space-y-8">

            {/* PENDING VOTES (AI-extracted) */}
            {pendingVotes.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                  <h2 className="font-mono text-sm text-yellow-400 uppercase tracking-wider">
                    Pending AI Votes — {pendingVotes.length} awaiting review
                  </h2>
                  <span className="font-mono text-[10px] text-gray-600">Approve to publish to Council Tracker · Reject to permanently remove</span>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Approve all ${pendingVotes.length} pending votes?`)) return;
                      try {
                        await api.post('/admin/votes/bulk-approve');
                        setPendingVotes([]);
                        toast.success(`All ${pendingVotes.length} votes approved`);
                        fetchData();
                      } catch { toast.error('Bulk approve failed'); }
                    }}
                    className="ml-auto px-3 py-1.5 bg-green-800 hover:bg-green-700 text-white font-mono text-xs rounded transition-colors"
                  >
                    APPROVE ALL
                  </button>
                </div>
                <div className="space-y-2">
                  {pendingVotes.map(v => (
                    <div key={v.id} className="bg-[#111] border border-yellow-900/30 rounded p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-mono px-2 py-0.5 rounded border ${
                              (v.result||'').includes('CARRIED')
                                ? 'text-green-400 border-green-900/40 bg-green-900/10'
                                : 'text-red-400 border-red-900/40 bg-red-900/10'
                            }`}>{v.result || '?'}</span>
                            <span className="text-gray-600 font-mono text-[10px]">{v.date}</span>
                            {v.pdf_link && <span className="text-gray-700 font-mono text-[10px] truncate max-w-[200px]">{v.pdf_link}</span>}
                          </div>
                          <p className="text-white text-sm mb-2">{v.motion}</p>
                          <div className="flex flex-wrap gap-3 text-xs font-mono">
                            {v.yes_votes?.length > 0 && (
                              <span className="text-green-500">{v.yes_votes.length} YES: {v.yes_votes.join(', ')}</span>
                            )}
                            {v.no_votes?.length > 0 && (
                              <span className="text-red-400">{v.no_votes.length} NO: {v.no_votes.join(', ')}</span>
                            )}
                            {(!v.yes_votes?.length && !v.no_votes?.length) && (
                              <span className="text-gray-600">No individual votes recorded</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={async () => {
                              try {
                                await api.patch(`/admin/votes/${v.id}/review`, { action: 'approve' });
                                setPendingVotes(prev => prev.filter(pv => pv.id !== v.id));
                                setVotes(prev => [...prev, { ...v, review_status: 'approved' }]);
                                toast.success('Vote approved — now live on Council Tracker');
                              } catch { toast.error('Failed to approve'); }
                            }}
                            className="px-3 py-1.5 bg-green-700 hover:bg-green-600 text-white font-mono text-xs rounded transition-colors"
                            data-testid={`approve-vote-${v.id}`}
                          >
                            APPROVE
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await api.patch(`/admin/votes/${v.id}/review`, { action: 'reject' });
                                setPendingVotes(prev => prev.filter(pv => pv.id !== v.id));
                                toast.success('Vote rejected — will not reappear on re-audit');
                              } catch { toast.error('Failed to reject'); }
                            }}
                            className="px-3 py-1.5 bg-zinc-700 hover:bg-red-800 text-gray-300 font-mono text-xs rounded transition-colors"
                            data-testid={`reject-vote-${v.id}`}
                          >
                            REJECT
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approved votes management */}
            <VotesTab 
              votes={votes}
              setShowVoteModal={setShowVoteModal}
              deleteVote={deleteVote}
            />

            {/* Bulk JSON Import */}
            <BulkVoteImport onImport={async (json) => {
              try {
                const res = await api.post('/admin/votes/bulk-upload', { votes: json });
                toast.success(`Imported ${res.data.inserted} of ${res.data.total} votes`);
                if (res.data.errors?.length) toast.warn(`${res.data.errors.length} errors — check console`);
                fetchData();
              } catch (e) { toast.error(e?.response?.data?.detail || 'Import failed'); }
            }} />
          </div>
        )}

        {/* KEYWORDS TAB */}
        {activeTab === 'keywords' && (
          <KeywordsTab
            keywords={keywords}
            newKeyword={newKeyword}
            setNewKeyword={setNewKeyword}
            addKeyword={addKeyword}
            deleteKeyword={deleteKeyword}
          />
        )}



        {/* Badges & Analytics Tab */}
        {activeTab === 'badges' && (
          <div className="space-y-6">

            {/* Delete Confirm Modal */}
            {confirmDeleteBadge && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
                <div className="bg-[#111] border border-red-800/60 rounded p-6 max-w-sm w-full mx-4 shadow-2xl">
                  <h3 className="font-mono text-sm text-red-400 uppercase tracking-wider mb-2">Confirm Delete</h3>
                  <p className="text-white mb-1">Delete <strong>"{confirmDeleteBadge.name}"</strong>?</p>
                  <p className="text-gray-500 text-xs font-mono mb-5">This is a soft delete — the badge can be restored from the "Show Deleted" list.</p>
                  <div className="flex gap-3">
                    <button
                      onClick={async () => {
                        try {
                          await api.delete(`/admin/badges/${confirmDeleteBadge.id}`);
                          toast.success('Badge deleted');
                          fetchBadges();
                        } catch {
                          toast.error('Failed to delete badge');
                        } finally {
                          setConfirmDeleteBadge(null);
                        }
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-500 text-white font-mono text-sm py-2 px-4 transition-colors"
                    >
                      YES, DELETE
                    </button>
                    <button
                      onClick={() => setConfirmDeleteBadge(null)}
                      className="flex-1 bg-[#2a2a2a] hover:bg-[#333] text-gray-300 font-mono text-sm py-2 px-4 transition-colors"
                    >
                      CANCEL
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Header with Create Button */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-2xl font-bold">Badge Management & Analytics</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDeletedBadges(s => !s)}
                  className={`font-mono text-xs px-3 py-2 border transition-colors ${showDeletedBadges ? 'border-red-600 text-red-400 bg-red-900/20' : 'border-[#2a2a2a] text-gray-500 hover:text-white'}`}
                >
                  {showDeletedBadges ? '▲ HIDE DELETED' : '▼ SHOW DELETED'}
                  {badges.filter(b => b.deleted_at).length > 0 && (
                    <span className="ml-1 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                      {badges.filter(b => b.deleted_at).length}
                    </span>
                  )}
                </button>
                <Link
                  to="/admin/badges/create"
                  className="bg-green-500 hover:bg-green-600 text-black px-4 py-2 rounded font-mono text-sm uppercase tracking-wider transition-colors"
                >
                  + CREATE BADGE
                </Link>
              </div>
            </div>

            {/* Category Management */}
            <div className="bg-gray-900 rounded p-6">
              <h3 className="text-xl font-bold mb-4">Badge Categories / Organization</h3>
              <p className="text-gray-400 text-sm mb-4">Create custom categories to organize your badges (e.g., "Coffee Collection", "Summer 2025")</p>
              
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  placeholder="Category Name (e.g., Coffee Collection)"
                  className="flex-1 bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded"
                  onKeyPress={async (e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      try {
                        await api.post(`/admin/badges/categories?name=${encodeURIComponent(e.target.value.trim())}&description=Custom category`);
                        toast.success('Category created!');
                        e.target.value = '';
                        // Reload badges to refresh
                        fetchBadges();
                      } catch (error) {
                        toast.error('Failed to create category');
                      }
                    }
                  }}
                />
                <button
                  onClick={async (e) => {
                    const input = e.target.previousElementSibling;
                    if (input.value.trim()) {
                      try {
                        await api.post(`/admin/badges/categories?name=${encodeURIComponent(input.value.trim())}&description=Custom category`);
                        toast.success('Category created!');
                        input.value = '';
                        fetchBadges();
                      } catch (error) {
                        toast.error('Failed to create category');
                      }
                    }
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded font-mono text-sm"
                >
                  Add Category
                </button>
              </div>
              
              <div className="text-xs text-gray-500 font-mono">
                Default categories: Yearly Challenges, Location Explorer, Activity, Collections, Official
              </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-900 p-4 rounded">
                <p className="text-gray-500 text-xs font-mono uppercase mb-1">Total Badges</p>
                <p className="text-2xl font-bold text-white">{badges.length}</p>
              </div>
              <div className="bg-gray-900 p-4 rounded">
                <p className="text-gray-500 text-xs font-mono uppercase mb-1">Active</p>
                <p className="text-2xl font-bold text-green-400">
                  {badges.filter(b => b.is_active).length}
                </p>
              </div>
              <div className="bg-gray-900 p-4 rounded">
                <p className="text-gray-500 text-xs font-mono uppercase mb-1">Sponsored</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {badges.filter(b => b.is_sponsored).length}
                </p>
              </div>
              <div className="bg-gray-900 p-4 rounded">
                <p className="text-gray-500 text-xs font-mono uppercase mb-1">Total Awarded</p>
                <p className="text-2xl font-bold text-blue-400">
                  {badges.reduce((sum, b) => sum + (b.current_supply || 0), 0)}
                </p>
              </div>
            </div>

            {/* Business Intelligence: Hotspots */}
            <div className="bg-gray-900 rounded p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl font-bold">🔥 BUSINESS INTELLIGENCE: HOTSPOTS</h3>
                  <p className="text-gray-400 text-sm font-mono">
                    Top venues where users check in - click venue to see visitor details
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await api.get('/admin/analytics/checkins');
                      setCheckins(res.data?.checkins || []);
                      setSelectedHotspot('all');
                    } catch (e) {
                      toast.error('Failed to load check-in details');
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-mono uppercase"
                >
                  View All Check-ins
                </button>
              </div>
              
              {hotspots.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Venue</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Category</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Address</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-500">Check-ins</th>
                        <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-500">Unique Visitors</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hotspots.slice(0, 20).map((hotspot, idx) => (
                        <tr key={idx} className="border-b border-gray-800 hover:bg-gray-800/50">
                          <td className="py-3 px-4 font-medium text-white">{hotspot.venue_name || 'Unknown'}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">{hotspot.venue_category || '-'}</td>
                          <td className="py-3 px-4 text-gray-400 text-sm">{hotspot.venue_address || '-'}</td>
                          <td className="py-3 px-4 text-right font-bold text-green-400">{hotspot.total_checkins}</td>
                          <td className="py-3 px-4 text-right text-blue-400">{hotspot.unique_visitors}</td>
                          <td className="py-3 px-4">
                            <button
                              onClick={async () => {
                                try {
                                  const res = await api.get(`/admin/analytics/checkins?venue_name=${encodeURIComponent(hotspot.venue_name)}`);
                                  setCheckins(res.data?.checkins || []);
                                  setSelectedHotspot(hotspot.venue_name);
                                } catch (e) {
                                  toast.error('Failed to load check-in details');
                                }
                              }}
                              className="text-blue-400 hover:text-blue-300 text-xs font-mono uppercase"
                            >
                              View Details →
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No check-ins yet. Users will appear here once they start checking in!</p>
              )}

              {/* Check-in Details Modal */}
              {selectedHotspot && (
                <div className="mt-6 bg-gray-950 border border-gray-800 rounded p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-bold text-white">
                      {selectedHotspot === 'all' ? 'All Check-ins' : `Check-ins at ${selectedHotspot}`}
                    </h4>
                    <button
                      onClick={() => { setSelectedHotspot(null); setCheckins([]); }}
                      className="text-gray-500 hover:text-white"
                    >
                      ✕ Close
                    </button>
                  </div>
                  
                  {checkins.length > 0 ? (
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-gray-950">
                          <tr className="border-b border-gray-800">
                            <th className="text-left py-2 px-3 font-mono text-xs uppercase text-gray-500">User</th>
                            <th className="text-left py-2 px-3 font-mono text-xs uppercase text-gray-500">Venue</th>
                            <th className="text-left py-2 px-3 font-mono text-xs uppercase text-gray-500">Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {checkins.map((ci, idx) => (
                            <tr key={idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                              <td className="py-2 px-3">
                                <div className="text-white text-sm">{ci.user_name}</div>
                                {ci.user_email && (
                                  <div className="text-gray-500 text-xs">{ci.user_email}</div>
                                )}
                              </td>
                              <td className="py-2 px-3 text-gray-400 text-sm">{ci.venue_name}</td>
                              <td className="py-2 px-3 text-gray-400 text-xs font-mono">
                                {ci.check_in_time ? new Date(ci.check_in_time).toLocaleString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No check-ins found</p>
                  )}
                </div>
              )}
            </div>

            {/* Badge Award / Remove Tool */}
            <div className="bg-gray-900 rounded p-6">
              <h3 className="text-xl font-bold mb-1">BADGE MANAGEMENT TOOL</h3>
              <p className="text-gray-500 text-xs font-mono mb-5">Manually award or remove a badge from a specific user. Awarding also grants the badge's points.</p>

              {/* Mode Toggle */}
              <div className="flex gap-2 mb-5">
                <button
                  onClick={() => { setBadgeMgrMode('award'); setBadgeMgrUser(null); setBadgeMgrResults([]); setBadgeMgrQuery(''); setBadgeMgrSelectedCode(''); }}
                  className={`font-mono text-xs px-4 py-2 border transition-colors ${badgeMgrMode === 'award' ? 'border-green-500 bg-green-500/10 text-green-400' : 'border-[#2a2a2a] text-gray-500 hover:text-white'}`}
                  data-testid="badge-mgr-award-tab"
                >
                  AWARD BADGE
                </button>
                <button
                  onClick={() => { setBadgeMgrMode('remove'); setBadgeMgrUser(null); setBadgeMgrResults([]); setBadgeMgrQuery(''); setBadgeMgrUserBadges([]); }}
                  className={`font-mono text-xs px-4 py-2 border transition-colors ${badgeMgrMode === 'remove' ? 'border-red-500 bg-red-500/10 text-red-400' : 'border-[#2a2a2a] text-gray-500 hover:text-white'}`}
                  data-testid="badge-mgr-remove-tab"
                >
                  REMOVE BADGE
                </button>
              </div>

              {/* User Search */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={badgeMgrQuery}
                  onChange={e => setBadgeMgrQuery(e.target.value)}
                  placeholder="Search by email or display name..."
                  className="flex-1 bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 font-mono focus:outline-none focus:border-[#C5A52A]"
                  data-testid="badge-mgr-search-input"
                  onKeyDown={async e => {
                    if (e.key === 'Enter') {
                      setBadgeMgrLoading(true);
                      try {
                        const res = await api.get(`/admin/users/search?q=${encodeURIComponent(badgeMgrQuery)}`);
                        setBadgeMgrResults(res.data?.users || []);
                        setBadgeMgrUser(null);
                      } catch { toast.error('Search failed'); }
                      finally { setBadgeMgrLoading(false); }
                    }
                  }}
                />
                <button
                  onClick={async () => {
                    if (!badgeMgrQuery.trim()) return;
                    setBadgeMgrLoading(true);
                    try {
                      const res = await api.get(`/admin/users/search?q=${encodeURIComponent(badgeMgrQuery)}`);
                      setBadgeMgrResults(res.data?.users || []);
                      setBadgeMgrUser(null);
                    } catch { toast.error('Search failed'); }
                    finally { setBadgeMgrLoading(false); }
                  }}
                  className="bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-mono text-xs px-4 py-2 transition-colors"
                  data-testid="badge-mgr-search-btn"
                  disabled={badgeMgrLoading}
                >
                  {badgeMgrLoading ? '...' : 'SEARCH'}
                </button>
              </div>

              {/* Search Results */}
              {badgeMgrResults.length > 0 && !badgeMgrUser && (
                <div className="border border-[#2a2a2a] mb-4">
                  {badgeMgrResults.map(u => (
                    <button
                      key={u.id}
                      onClick={async () => {
                        setBadgeMgrUser(u);
                        setBadgeMgrResults([]);
                        if (badgeMgrMode === 'remove') {
                          setBadgeMgrLoading(true);
                          try {
                            const res = await api.get(`/admin/users/${u.id}/badges`);
                            setBadgeMgrUserBadges(res.data?.badges || []);
                          } catch { toast.error('Failed to load user badges'); }
                          finally { setBadgeMgrLoading(false); }
                        }
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-[#1a1a1a] border-b border-[#1a1a1a] last:border-0 transition-colors"
                    >
                      <div className="font-medium text-white text-sm">{u.display_name}</div>
                      <div className="text-gray-500 text-xs font-mono">{u.email} — {u.ward || 'No ward'}</div>
                    </button>
                  ))}
                </div>
              )}
              {badgeMgrResults.length === 0 && badgeMgrQuery && !badgeMgrUser && !badgeMgrLoading && (
                <p className="text-gray-600 text-xs font-mono mb-4">No users found. Try a different search term.</p>
              )}

              {/* Selected User + Action */}
              {badgeMgrUser && (
                <div className="border border-[#2a2a2a] p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="font-bold text-white">{badgeMgrUser.display_name}</div>
                      <div className="text-gray-500 text-xs font-mono">{badgeMgrUser.email}</div>
                    </div>
                    <button
                      onClick={() => { setBadgeMgrUser(null); setBadgeMgrUserBadges([]); setBadgeMgrSelectedCode(''); }}
                      className="text-gray-600 hover:text-white text-xs font-mono"
                    >
                      ✕ CHANGE USER
                    </button>
                  </div>

                  {/* AWARD mode */}
                  {badgeMgrMode === 'award' && (
                    <div className="flex gap-2">
                      <select
                        value={badgeMgrSelectedCode}
                        onChange={e => setBadgeMgrSelectedCode(e.target.value)}
                        className="flex-1 bg-[#111] border border-[#2a2a2a] text-white text-sm px-3 py-2 font-mono focus:outline-none focus:border-green-500"
                        data-testid="badge-mgr-badge-select"
                      >
                        <option value="">Select badge to award...</option>
                        {badges.filter(b => !b.deleted_at && b.status !== 'deleted').map(b => (
                          <option key={b.id} value={b.code}>
                            {b.name} ({b.code}){b.reward_points ? ` — +${b.reward_points}pts` : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={async () => {
                          if (!badgeMgrSelectedCode) { toast.error('Select a badge first'); return; }
                          setBadgeMgrLoading(true);
                          try {
                            const res = await api.post('/admin/badges/assign', {
                              user_id: badgeMgrUser.id,
                              badge_code: badgeMgrSelectedCode
                            });
                            toast.success(res.data?.message || 'Badge awarded!');
                            setBadgeMgrSelectedCode('');
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Failed to award badge');
                          } finally { setBadgeMgrLoading(false); }
                        }}
                        disabled={badgeMgrLoading || !badgeMgrSelectedCode}
                        className="bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-mono text-xs px-5 py-2 transition-colors"
                        data-testid="badge-mgr-award-btn"
                      >
                        {badgeMgrLoading ? '...' : 'AWARD'}
                      </button>
                    </div>
                  )}

                  {/* REMOVE mode */}
                  {badgeMgrMode === 'remove' && (
                    <div>
                      {badgeMgrLoading && <p className="text-gray-500 text-xs font-mono">Loading badges...</p>}
                      {!badgeMgrLoading && badgeMgrUserBadges.length === 0 && (
                        <p className="text-gray-600 text-xs font-mono">This user has no badges.</p>
                      )}
                      {badgeMgrUserBadges.length > 0 && (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {badgeMgrUserBadges.map(ub => (
                            <div key={ub.user_badge_id || ub.code} className="flex items-center justify-between bg-[#111] px-3 py-2 border border-[#1a1a1a]">
                              <div className="flex items-center gap-3">
                                {ub.icon_url && ub.icon_url.startsWith('http') ? (
                                  <img src={ub.icon_url} alt={ub.name} className="w-7 h-7 object-contain" />
                                ) : (
                                  <div className="text-lg">{ub.icon_url || '🏅'}</div>
                                )}
                                <div>
                                  <div className="text-white text-sm font-medium">{ub.name}</div>
                                  <div className="text-gray-600 text-xs font-mono">{ub.code}</div>
                                </div>
                              </div>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Remove "${ub.name}" from ${badgeMgrUser.display_name}?`)) return;
                                  setBadgeMgrLoading(true);
                                  try {
                                    await api.post('/admin/badges/revoke', {
                                      user_id: badgeMgrUser.id,
                                      badge_code: ub.code
                                    });
                                    toast.success(`Removed ${ub.name}`);
                                    setBadgeMgrUserBadges(prev => prev.filter(b => b.code !== ub.code));
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Failed to remove badge');
                                  } finally { setBadgeMgrLoading(false); }
                                }}
                                className="text-red-500 hover:text-red-400 font-mono text-xs px-3 py-1 border border-red-800/40 hover:border-red-600 transition-colors"
                                data-testid={`badge-mgr-remove-${ub.code}`}
                              >
                                REMOVE
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* All Badges */}
            <div className="bg-gray-900 rounded p-6">
              <h3 className="text-xl font-bold mb-4">ALL BADGES</h3>
              
              {badges.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Icon</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Name</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Type</th>
                        <th className="text-center py-3 px-4 font-mono text-xs uppercase text-gray-500">Earned</th>
                        <th className="text-center py-3 px-4 font-mono text-xs uppercase text-gray-500">Rarity</th>
                        <th className="text-center py-3 px-4 font-mono text-xs uppercase text-gray-500">Status</th>
                        <th className="text-center py-3 px-4 font-mono text-xs uppercase text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {badges.filter(b => showDeletedBadges ? !!b.deleted_at : !b.deleted_at).map(badge => (
                        <tr 
                          key={badge.id} 
                          className="border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer"
                          onDoubleClick={() => {
                            // Navigate to edit page with badge data
                            navigate(`/admin/badges/edit/${badge.id}`);
                          }}
                          title="Double-click to edit"
                        >
                          <td className="py-3 px-4">
                            {badge.icon_url && badge.icon_url.startsWith('http') ? (
                              <img 
                                src={badge.icon_url} 
                                alt={badge.name}
                                className="w-12 h-12 object-contain"
                              />
                            ) : (
                              <div className="text-2xl">{badge.icon_url || '🏅'}</div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium text-white">{badge.name}</div>
                            <div className="text-xs text-gray-500 font-mono">{badge.code}</div>
                            {badge.venue_name && (
                              <div className="text-xs text-gray-600 mt-1">{badge.venue_name}</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-400 text-sm">
                            {badge.badge_type || 'location'}
                            {badge.is_yearly_challenge && (
                              <div className="text-xs text-yellow-500">🏅 Yearly Challenge</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-sm font-mono font-bold">
                              {badge.current_supply || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <select
                              value={badge.rarity || 'standard'}
                              onChange={async (e) => {
                                try {
                                  await api.put(`/admin/badges/${badge.id}/rarity?rarity=${e.target.value}`);
                                  toast.success('Rarity updated');
                                  fetchBadges();
                                } catch (error) {
                                  toast.error('Failed to update rarity');
                                }
                              }}
                              className="bg-gray-800 border border-gray-700 text-white text-xs px-2 py-1 rounded"
                            >
                              <option value="standard">Standard</option>
                              <option value="special">Special</option>
                              <option value="official">Official</option>
                              <option value="legacy">Legacy</option>
                            </select>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <select
                              value={badge.status || 'active'}
                              onChange={async (e) => {
                                try {
                                  await api.put(`/admin/badges/${badge.id}/status?status=${e.target.value}`);
                                  toast.success('Status updated');
                                  fetchBadges();
                                } catch (error) {
                                  toast.error('Failed to update status');
                                }
                              }}
                              className="bg-gray-800 border border-gray-700 text-white text-xs px-2 py-1 rounded"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="hidden">Hidden</option>
                            </select>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => navigate(`/admin/badges/edit/${badge.id}`)}
                                className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 rounded border border-blue-400/30 hover:bg-blue-400/10 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  const embedCode = `<iframe src="${window.location.origin}/embed/badge/${badge.code}" width="300" height="130" frameborder="0" style="border:none;border-radius:8px;overflow:hidden;"></iframe>`;
                                  navigator.clipboard.writeText(embedCode);
                                  toast.success('Embed code copied! Paste it on any website.');
                                }}
                                className="text-purple-400 hover:text-purple-300 text-xs px-2 py-1 rounded border border-purple-400/30 hover:bg-purple-400/10 transition-colors"
                                title="Copy embed code for external sites"
                              >
                                Embed
                              </button>
                              <button
                                onClick={() => setConfirmDeleteBadge(badge)}
                                className="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded border border-red-400/30 hover:bg-red-400/10 transition-colors"
                              >
                                Delete
                              </button>
                              {badge.deleted_at && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await api.post(`/admin/badges/${badge.id}/restore`);
                                      toast.success('Badge restored');
                                      fetchBadges();
                                    } catch {
                                      toast.error('Failed to restore badge');
                                    }
                                  }}
                                  className="text-green-400 hover:text-green-300 text-xs px-2 py-1 rounded border border-green-400/30 hover:bg-green-400/10 transition-colors"
                                >
                                  Restore
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No badges created yet. Create your first badge!</p>
              )}
            </div>
          </div>
        )}



        {/* ROLES TAB - Councillor & Mayor Badge Assignment */}
        {activeTab === 'roles' && (
          <div>
            <RoleManagement />
          </div>
        )}

        {/* STORE TAB */}
        {activeTab === 'store' && (
          <StoreTab />
        )}

        {/* WARDS TAB */}
        {/* wards rendered inside 'wards & councillors' tab above */}

        {/* REALTOR ADS — now merged into ADS tab below */}

        {/* FOUNDERS CORNER TAB */}
        {activeTab === 'founders' && (
          <FoundersCornerTab />
        )}

        {/* TAKEDOWNS TAB */}
        {activeTab === 'takedowns' && (
          <TakedownsTab 
            takedowns={takedowns} 
            updateTakedownStatus={updateTakedownStatus} 
          />
        )}

        {/* ISSUES TAB - Issue Categories & Ward Stats */}
        {activeTab === 'issues' && (
          <div className="space-y-8">
            {/* Ward Statistics Summary */}
            {wardStats && (
              <div className="bg-gray-900 border border-gray-800 p-6 rounded-sm">
                <h3 className="text-xl font-bold text-white mb-4">📊 Issue Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <div className="bg-gray-950 p-4 rounded text-center">
                    <div className="text-3xl font-bold text-white">{wardStats.totals?.total || 0}</div>
                    <div className="text-gray-500 text-xs font-mono uppercase">Total Issues</div>
                  </div>
                  <div className="bg-gray-950 p-4 rounded text-center">
                    <div className="text-3xl font-bold text-yellow-400">{wardStats.totals?.reported || 0}</div>
                    <div className="text-gray-500 text-xs font-mono uppercase">Reported</div>
                  </div>
                  <div className="bg-gray-950 p-4 rounded text-center">
                    <div className="text-3xl font-bold text-blue-400">{wardStats.totals?.verified || 0}</div>
                    <div className="text-gray-500 text-xs font-mono uppercase">Verified</div>
                  </div>
                  <div className="bg-gray-950 p-4 rounded text-center">
                    <div className="text-3xl font-bold text-green-400">{wardStats.totals?.fixed || 0}</div>
                    <div className="text-gray-500 text-xs font-mono uppercase">Fixed</div>
                  </div>
                  <div className="bg-gray-950 p-4 rounded text-center">
                    <div className="text-3xl font-bold text-green-500">{wardStats.totals?.fix_rate || 0}%</div>
                    <div className="text-gray-500 text-xs font-mono uppercase">Fix Rate</div>
                  </div>
                </div>
              </div>
            )}

            {/* Ward Report Generator */}
            <div className="bg-gray-900 border border-gray-800 p-6 rounded-sm">
              <h3 className="text-xl font-bold text-white mb-4">📋 Generate Councillor Report</h3>
              <p className="text-gray-400 text-sm mb-4">
                Export a summary report for any ward. Perfect for semi-annual councillor updates.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-gray-500 text-xs font-mono uppercase mb-1">Ward</label>
                  <select
                    id="reportWard"
                    className="w-full bg-gray-950 border border-gray-700 text-white px-3 py-2 rounded-sm"
                    defaultValue=""
                  >
                    <option value="">Select Ward</option>
                    {councillors.map((c) => (
                      <option key={c.ward} value={c.ward}>{c.ward} - {c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-500 text-xs font-mono uppercase mb-1">Start Date</label>
                  <input
                    type="date"
                    id="reportStartDate"
                    className="w-full bg-gray-950 border border-gray-700 text-white px-3 py-2 rounded-sm"
                  />
                </div>
                <div>
                  <label className="block text-gray-500 text-xs font-mono uppercase mb-1">End Date</label>
                  <input
                    type="date"
                    id="reportEndDate"
                    className="w-full bg-gray-950 border border-gray-700 text-white px-3 py-2 rounded-sm"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={async () => {
                      const ward = document.getElementById('reportWard').value;
                      const startDate = document.getElementById('reportStartDate').value;
                      const endDate = document.getElementById('reportEndDate').value;
                      
                      if (!ward) {
                        toast.error('Please select a ward');
                        return;
                      }
                      
                      try {
                        const params = new URLSearchParams();
                        if (startDate) params.append('start_date', startDate);
                        if (endDate) params.append('end_date', endDate);
                        
                        // Download CSV
                        const response = await api.get(`/admin/ward-report/${encodeURIComponent(ward)}/csv?${params}`, {
                          responseType: 'blob'
                        });
                        
                        // Create download link
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `ward_report_${ward.replace(' ', '_')}.csv`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        
                        toast.success('Report downloaded!');
                      } catch (e) {
                        toast.error('Failed to generate report');
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 font-mono text-sm uppercase rounded-sm"
                  >
                    Download CSV
                  </button>
                </div>
              </div>
              <p className="text-gray-600 text-xs font-mono">
                Leave dates empty for last 6 months. Report includes: posts, issues, resolution rates, messages.
              </p>
            </div>

            {/* Issue Categories Management */}
            <div className="bg-gray-900 border border-gray-800 p-6 rounded-sm">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">Issue Categories</h3>
                  <p className="text-gray-500 text-sm">Define what types of issues users can report</p>
                </div>
                <button
                  onClick={() => {
                    setIssueCategoryForm({
                      id: '', group_name: '', name: '', verification_distance_meters: 100,
                      requires_checkin: true, icon: '', points_report: 5, points_verify: 3
                    });
                    setShowIssueCategoryModal(true);
                  }}
                  className="bg-green-500 hover:bg-green-600 text-black px-4 py-2 font-mono text-sm uppercase rounded-sm"
                >
                  + Add Category
                </button>
              </div>

              {/* Categories Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Group</th>
                      <th className="text-left py-3 px-4 font-mono text-xs uppercase text-gray-500">Name</th>
                      <th className="text-center py-3 px-4 font-mono text-xs uppercase text-gray-500">Icon</th>
                      <th className="text-center py-3 px-4 font-mono text-xs uppercase text-gray-500">Distance (m)</th>
                      <th className="text-center py-3 px-4 font-mono text-xs uppercase text-gray-500">Points</th>
                      <th className="text-center py-3 px-4 font-mono text-xs uppercase text-gray-500">Status</th>
                      <th className="text-right py-3 px-4 font-mono text-xs uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issueCategories.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="py-8 text-center text-gray-500">
                          No issue categories yet. Add some to enable issue reporting!
                        </td>
                      </tr>
                    ) : (
                      issueCategories.map((cat) => (
                        <tr key={cat.id} className={`border-b border-gray-800 ${!cat.is_active ? 'opacity-50' : ''}`}>
                          <td className="py-3 px-4">
                            <span className="bg-gray-800 text-gray-300 px-2 py-1 rounded text-xs font-mono">
                              {cat.group_name}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-white">{cat.name}</td>
                          <td className="py-3 px-4 text-center text-2xl">{cat.icon || '📍'}</td>
                          <td className="py-3 px-4 text-center text-gray-400 font-mono">{cat.verification_distance_meters}m</td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-green-400 text-xs">+{cat.points_report} report</span>
                            <span className="text-gray-600 mx-1">/</span>
                            <span className="text-blue-400 text-xs">+{cat.points_verify} verify</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            {cat.is_active ? (
                              <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded text-xs">ACTIVE</span>
                            ) : (
                              <span className="bg-gray-800 text-gray-500 px-2 py-1 rounded text-xs">INACTIVE</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => {
                                setIssueCategoryForm({
                                  id: cat.id,
                                  group_name: cat.group_name,
                                  name: cat.name,
                                  verification_distance_meters: cat.verification_distance_meters,
                                  requires_checkin: cat.requires_checkin,
                                  icon: cat.icon || '',
                                  points_report: cat.points_report,
                                  points_verify: cat.points_verify
                                });
                                setShowIssueCategoryModal(true);
                              }}
                              className="text-blue-400 hover:text-blue-300 text-xs font-mono uppercase mr-2"
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await api.put(`/admin/issue-categories/${cat.id}?is_active=${!cat.is_active}`);
                                  toast.success(cat.is_active ? 'Category deactivated' : 'Category activated');
                                  fetchData();
                                } catch (e) {
                                  toast.error('Failed to update category');
                                }
                              }}
                              className={`text-xs font-mono uppercase ${cat.is_active ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}`}
                            >
                              {cat.is_active ? 'Disable' : 'Enable'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Issue Category Modal */}
            {showIssueCategoryModal && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 border border-gray-800 rounded-sm p-6 max-w-lg w-full">
                  <h3 className="text-xl font-bold text-white mb-4">
                    {issueCategoryForm.id ? 'Edit Category' : 'Add Issue Category'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Group Name</label>
                        <input
                          type="text"
                          value={issueCategoryForm.group_name}
                          onChange={(e) => setIssueCategoryForm({...issueCategoryForm, group_name: e.target.value})}
                          placeholder="e.g., Street, Parks, Misc"
                          className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Icon (emoji)</label>
                        <input
                          type="text"
                          value={issueCategoryForm.icon}
                          onChange={(e) => setIssueCategoryForm({...issueCategoryForm, icon: e.target.value})}
                          placeholder="🕳️"
                          className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Category Name</label>
                      <input
                        type="text"
                        value={issueCategoryForm.name}
                        onChange={(e) => setIssueCategoryForm({...issueCategoryForm, name: e.target.value})}
                        placeholder="e.g., Potholes, Graffiti"
                        className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">
                        Verification Distance (meters)
                      </label>
                      <input
                        type="number"
                        value={issueCategoryForm.verification_distance_meters}
                        onChange={(e) => setIssueCategoryForm({...issueCategoryForm, verification_distance_meters: parseInt(e.target.value) || 100})}
                        className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                      />
                      <p className="text-gray-600 text-xs mt-1">How close must someone be to verify this issue?</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Points for Reporting</label>
                        <input
                          type="number"
                          value={issueCategoryForm.points_report}
                          onChange={(e) => setIssueCategoryForm({...issueCategoryForm, points_report: parseInt(e.target.value) || 5})}
                          className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Points for Verifying</label>
                        <input
                          type="number"
                          value={issueCategoryForm.points_verify}
                          onChange={(e) => setIssueCategoryForm({...issueCategoryForm, points_verify: parseInt(e.target.value) || 3})}
                          className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 mt-6">
                      <button
                        onClick={async () => {
                          try {
                            if (issueCategoryForm.id) {
                              // Update
                              const params = new URLSearchParams({
                                group_name: issueCategoryForm.group_name,
                                name: issueCategoryForm.name,
                                verification_distance_meters: issueCategoryForm.verification_distance_meters,
                                icon: issueCategoryForm.icon,
                                points_report: issueCategoryForm.points_report,
                                points_verify: issueCategoryForm.points_verify
                              });
                              await api.put(`/admin/issue-categories/${issueCategoryForm.id}?${params}`);
                              toast.success('Category updated!');
                            } else {
                              // Create
                              const params = new URLSearchParams({
                                group_name: issueCategoryForm.group_name,
                                name: issueCategoryForm.name,
                                verification_distance_meters: issueCategoryForm.verification_distance_meters,
                                icon: issueCategoryForm.icon || '',
                                points_report: issueCategoryForm.points_report,
                                points_verify: issueCategoryForm.points_verify
                              });
                              await api.post(`/admin/issue-categories?${params}`);
                              toast.success('Category created!');
                            }
                            setShowIssueCategoryModal(false);
                            fetchData();
                          } catch (e) {
                            toast.error('Failed to save category');
                          }
                        }}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-black px-4 py-2 font-mono text-sm uppercase rounded-sm"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setShowIssueCategoryModal(false)}
                        className="flex-1 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 font-mono text-sm uppercase rounded-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* REPORTS TAB */}
        {activeTab === 'reports' && (
          <ReportsTab 
            reports={reports} 
            handleReportAction={handleReportAction} 
          />
        )}

        {/* COMMUNITY LEADERS TAB */}
        {activeTab === 'leaders' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Team & Moderators</h2>
              <button
                onClick={() => setShowLeaderModal(true)}
                className="bg-green-500 hover:bg-green-600 text-black px-4 py-2 font-mono text-sm uppercase tracking-wider rounded-sm"
              >
                + Assign Leader
              </button>
            </div>

            {/* Admins Section */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-purple-400 mb-4 flex items-center gap-2">
                <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                Administrators
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {users.filter(u => u.role === 'admin').length === 0 ? (
                  <div className="bg-gray-900 border border-gray-800 p-4 rounded-sm">
                    <p className="text-gray-500 text-sm">No administrators</p>
                  </div>
                ) : (
                  users.filter(u => u.role === 'admin').map((admin) => (
                    <div key={admin.id} className="bg-purple-900/20 border border-purple-800 p-4 rounded-sm">
                      <h4 className="text-white font-bold">{admin.name}</h4>
                      <p className="text-gray-400 text-sm">{admin.email}</p>
                      <span className="inline-block mt-2 bg-purple-500/30 text-purple-300 px-2 py-1 text-xs font-mono rounded">
                        ADMIN
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Community Leaders Section */}
            <div>
              <h3 className="text-lg font-bold text-blue-400 mb-4 flex items-center gap-2" data-testid="community-leaders-heading">
                <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                Community Leaders
              </h3>
              <div className="bg-gray-900 border border-gray-800 p-4 rounded-sm mb-4">
                <p className="text-gray-400 text-sm">
                  <strong>Max 2 per ward.</strong> They can moderate posts in their assigned ward.
                </p>
                <p className="text-gray-500 text-xs mt-1" data-testid="community-leaders-total">
                  Total: {communityLeaders.length} leaders
                </p>
              </div>

              <div className="space-y-4">
                {communityLeaders.length === 0 ? (
                  <div className="bg-gray-900 border border-gray-800 p-6 rounded-sm text-center">
                    <p className="text-gray-500">No community leaders assigned yet</p>
                    <p className="text-gray-600 text-xs mt-2">Use “Assign Leader” or change a user’s role to Community Leader in the Users tab</p>
                  </div>
                ) : (
                  communityLeaders.map((leader) => (
                    <div key={leader.id} className="bg-blue-900/20 border border-blue-800 p-6 rounded-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-white font-bold mb-1">{leader.name}</h3>
                          <p className="text-gray-400 text-sm mb-1">{leader.email}</p>
                          <div className="flex gap-2 mt-2">
                            <span className="bg-blue-900/30 border border-blue-800 text-blue-400 px-2 py-1 text-xs font-mono rounded">
                              {leader.ward || 'No Ward'}
                            </span>
                            <span className="bg-gray-800 text-gray-400 px-2 py-1 text-xs font-mono rounded">
                              Since {new Date(leader.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveLeader(leader.id)}
                          className="bg-red-900/30 hover:bg-red-900/50 text-red-500 px-3 py-1 text-xs font-mono uppercase tracking-wider rounded-sm border border-red-800"
                          data-testid={`community-leader-remove-${leader.id}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Assign Leader Modal */}
            {showLeaderModal && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 border border-gray-800 rounded-sm p-6 max-w-md w-full">
                  <h3 className="text-xl font-bold text-white mb-4">Assign Community Leader</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Select User</label>
                      <select
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                      >
                        <option value="">-- Select User --</option>
                        {users.filter(u => u.role !== 'admin' && u.role !== 'community_leader').map(u => (
                          <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Assign to Ward</label>
                      <select
                        value={leaderWard}
                        onChange={(e) => setLeaderWard(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 text-white px-3 py-2 rounded-sm"
                      >
                        <option value="">-- Select Ward --</option>
                        {['Ward 1', 'Ward 2', 'Ward 3', 'Ward 4', 'Ward 5', 'Ward 6', 'Ward 7', 'Ward 8', 'Ward 9', 'Ward 10'].map(ward => (
                          <option key={ward} value={ward}>{ward}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex gap-2 mt-6">
                      <button
                        onClick={() => {
                          if (selectedUserId && leaderWard) {
                            handleAssignLeader(selectedUserId, leaderWard);
                          } else {
                            toast.error('Please select user and ward');
                          }
                        }}
                        className="flex-1 bg-green-500 hover:bg-green-600 text-black px-4 py-2 font-mono text-sm uppercase rounded-sm"
                      >
                        Assign
                      </button>
                      <button
                        onClick={() => {
                          setShowLeaderModal(false);
                          setSelectedUserId('');
                          setLeaderWard('');
                        }}
                        className="flex-1 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 font-mono text-sm uppercase rounded-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </main>

      {/* Ad Modal */}
      {showAdModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 border border-gray-800 w-full max-w-lg rounded-sm">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white uppercase font-mono">
                {editingAd ? 'Edit Ad' : 'Create Ad'}
              </h2>
              <button onClick={() => setShowAdModal(false)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            
            <form onSubmit={saveAd} className="p-6 space-y-4">
              <input
                type="text"
                value={adForm.title}
                onChange={(e) => setAdForm({ ...adForm, title: e.target.value })}
                placeholder="Ad Title"
                required
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
              />
              <textarea
                value={adForm.content}
                onChange={(e) => setAdForm({ ...adForm, content: e.target.value })}
                placeholder="Ad Content"
                required
                rows="4"
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm resize-none"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-mono text-gray-400 mb-2 uppercase tracking-wider">
                    Ad Type
                  </label>
                  <select
                    value={adForm.ad_type}
                    onChange={(e) => setAdForm({ ...adForm, ad_type: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                  >
                    <option value="standard">Standard ($40/month)</option>
                    <option value="main_sponsor">Main Site Sponsor ($100/week)</option>
                    <option value="email_sponsor">Email Tracker Sponsor ($500/month)</option>
                    <option value="donation">Donation/Free</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={adForm.is_affiliate}
                      onChange={(e) => setAdForm({ ...adForm, is_affiliate: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-mono text-gray-400 uppercase">Affiliate Link</span>
                  </label>
                </div>
              </div>
              
              <input
                type="url"
                value={adForm.link}
                onChange={(e) => setAdForm({ ...adForm, link: e.target.value })}
                placeholder="Link URL (optional)"
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
              />
              
              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-mono text-gray-400 mb-2 uppercase tracking-wider">
                  Ad Image
                </label>
                <div className="flex gap-4 items-start">
                  {adForm.image_url && (
                    <img 
                      src={adForm.image_url} 
                      alt="Preview" 
                      className="w-24 h-24 object-cover border border-gray-700 rounded"
                    />
                  )}
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const response = await api.post('/admin/upload-image', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setAdForm({ ...adForm, image_url: response.data.image_url });
                          toast.success('Image uploaded!');
                        } catch (error) {
                          toast.error('Failed to upload image');
                        }
                      }}
                      className="hidden"
                      id="adImageUpload"
                    />
                    <label
                      htmlFor="adImageUpload"
                      className="block text-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-sm cursor-pointer font-mono text-sm uppercase"
                    >
                      Upload Image
                    </label>
                    <input
                      type="text"
                      value={adForm.image_url}
                      onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })}
                      placeholder="Or paste image URL (https://...)"
                      className="w-full px-3 py-2 bg-gray-950 border border-gray-700 text-white rounded-sm text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowAdModal(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-sm uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-4 rounded-sm uppercase"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Councillor Modal */}
      {showCouncillorModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-gray-900 border border-gray-800 w-full max-w-2xl rounded-sm my-8">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white uppercase font-mono">
                {councillorForm.id ? 'Edit Councillor' : 'Add Councillor'}
              </h2>
              <button onClick={() => setShowCouncillorModal(false)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            
            <form onSubmit={saveCouncillor} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  value={councillorForm.id}
                  onChange={(e) => setCouncillorForm({ ...councillorForm, id: e.target.value })}
                  placeholder="ID (e.g., ward1)"
                  required
                  disabled={!!councillors.find(c => c.id === councillorForm.id)}
                  className="px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm disabled:opacity-50"
                />
                <input
                  type="text"
                  value={councillorForm.ward}
                  onChange={(e) => setCouncillorForm({ ...councillorForm, ward: e.target.value })}
                  placeholder="Ward"
                  required
                  className="px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                />
              </div>
              <input
                type="text"
                value={councillorForm.name}
                onChange={(e) => setCouncillorForm({ ...councillorForm, name: e.target.value })}
                placeholder="Full Name"
                required
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
              />
              <input
                type="text"
                value={councillorForm.match_name}
                onChange={(e) => setCouncillorForm({ ...councillorForm, match_name: e.target.value })}
                placeholder="Match Name (for votes)"
                required
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="email"
                  value={councillorForm.email}
                  onChange={(e) => setCouncillorForm({ ...councillorForm, email: e.target.value })}
                  placeholder="Email"
                  required
                  className="px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                />
                <input
                  type="tel"
                  value={councillorForm.phone}
                  onChange={(e) => setCouncillorForm({ ...councillorForm, phone: e.target.value })}
                  placeholder="Phone"
                  className="px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                />
              </div>
              <textarea
                value={councillorForm.bio}
                onChange={(e) => setCouncillorForm({ ...councillorForm, bio: e.target.value })}
                placeholder="Bio"
                rows="3"
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm resize-none"
              />
              <input
                type="text"
                value={councillorForm.postal_codes}
                onChange={(e) => setCouncillorForm({ ...councillorForm, postal_codes: e.target.value })}
                placeholder="Postal Codes (comma-separated, e.g., S4P,S4R,S4S)"
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
              />
              
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-mono text-gray-400 mb-2 uppercase tracking-wider">
                  Councillor Image
                </label>
                <div className="flex gap-4 items-center">
                  {councillorForm.image_url && (
                    <img 
                      src={councillorForm.image_url} 
                      alt="Preview" 
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-700"
                    />
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                      className="hidden"
                      id="imageUpload"
                    />
                    <label
                      htmlFor="imageUpload"
                      className={
                        'block text-center px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-sm cursor-pointer font-mono text-sm uppercase ' +
                        (uploadingImage ? 'opacity-50 cursor-not-allowed' : '')
                      }
                    >
                      {uploadingImage ? 'Uploading...' : 'Upload Image'}
                    </label>
                    <p className="text-xs text-gray-500 mt-1 font-mono">Or paste URL below</p>
                  </div>
                </div>
                <input
                  type="text"
                  value={councillorForm.image_url}
                  onChange={(e) => setCouncillorForm({ ...councillorForm, image_url: e.target.value })}
                  placeholder="Or paste image URL"
                  className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm mt-2"
                />
              </div>
              
              {/* Responsiveness Metrics */}
              <div>
                <label className="block text-sm font-mono text-gray-400 mb-2 uppercase tracking-wider">
                  Responsiveness Metrics
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-gray-500 mb-1">Emails Delivered</label>
                    <input
                      type="number"
                      value={councillorForm.emails_delivered || 0}
                      onChange={(e) => setCouncillorForm({ ...councillorForm, emails_delivered: parseInt(e.target.value) || 0 })}
                      min="0"
                      className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-500 mb-1">Emails Responded</label>
                    <input
                      type="number"
                      value={councillorForm.emails_responded || 0}
                      onChange={(e) => setCouncillorForm({ ...councillorForm, emails_responded: parseInt(e.target.value) || 0 })}
                      min="0"
                      className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-gray-500 mb-1">Redemption (Late Reply)</label>
                    <input
                      type="number"
                      value={councillorForm.emails_redemption || 0}
                      onChange={(e) => setCouncillorForm({ ...councillorForm, emails_redemption: parseInt(e.target.value) || 0 })}
                      min="0"
                      className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowCouncillorModal(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-sm uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-4 rounded-sm uppercase"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vote Modal */}
      {showVoteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-gray-900 border border-gray-800 w-full max-w-3xl rounded-sm my-8">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white uppercase font-mono">Add Vote Record</h2>
              <button onClick={() => setShowVoteModal(false)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            
            <form onSubmit={saveVote} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  value={voteForm.date}
                  onChange={(e) => setVoteForm({ ...voteForm, date: e.target.value })}
                  placeholder="Date (e.g., Dec 15, 2025)"
                  required
                  className="px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                />
                <select
                  value={voteForm.result}
                  onChange={(e) => setVoteForm({ ...voteForm, result: e.target.value })}
                  required
                  className="px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
                >
                  <option value="">Select Result</option>
                  <option value="CARRIED">CARRIED</option>
                  <option value="LOST">LOST</option>
                </select>
              </div>
              
              <input
                type="text"
                value={voteForm.motion}
                onChange={(e) => setVoteForm({ ...voteForm, motion: e.target.value })}
                placeholder="Motion title (e.g. BYLAW 2025-55 AMENDMENT)"
                required
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
              />

              <textarea
                value={voteForm.description}
                onChange={(e) => setVoteForm({ ...voteForm, description: e.target.value })}
                placeholder="Description — what does this motion actually do? (optional)"
                rows={3}
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm resize-none"
              />
              
              <input
                type="text"
                value={voteForm.pdf_link}
                onChange={(e) => setVoteForm({ ...voteForm, pdf_link: e.target.value })}
                placeholder="PDF link (optional)"
                className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white rounded-sm"
              />
              
              {/* Councillor Votes */}
              <div className="border border-gray-800 rounded-sm p-4">
                <h3 className="text-white font-bold mb-3 uppercase font-mono text-sm">Select Votes</h3>
                <div className="grid grid-cols-1 gap-3">
                  {councillors.map((c) => (
                    <div key={c.id} className="bg-gray-950 p-3 rounded-sm">
                      <div className="font-mono text-white text-sm mb-2">{c.match_name}</div>
                      <div className="grid grid-cols-5 gap-1">
                        <button
                          type="button"
                          onClick={() => toggleVote(c.match_name, 'yes')}
                          className={
                            'py-2 px-2 rounded-sm text-xs font-bold uppercase transition-colors ' +
                            (voteForm.yes_votes.includes(c.match_name)
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700')
                          }
                        >
                          YES
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleVote(c.match_name, 'no')}
                          className={
                            'py-2 px-2 rounded-sm text-xs font-bold uppercase transition-colors ' +
                            (voteForm.no_votes.includes(c.match_name)
                              ? 'bg-red-600 text-white'
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700')
                          }
                        >
                          NO
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleVote(c.match_name, 'abstained')}
                          className={
                            'py-2 px-2 rounded-sm text-xs font-bold uppercase transition-colors ' +
                            (voteForm.abstained_votes.includes(c.match_name)
                              ? 'bg-yellow-600 text-white'
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700')
                          }
                        >
                          ABS
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleVote(c.match_name, 'not_present')}
                          className={
                            'py-2 px-2 rounded-sm text-xs font-bold uppercase transition-colors ' +
                            (voteForm.not_present_votes.includes(c.match_name)
                              ? 'bg-gray-600 text-white'
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700')
                          }
                        >
                          N/P
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleVote(c.match_name, 'conflict')}
                          className={
                            'py-2 px-2 rounded-sm text-xs font-bold uppercase transition-colors ' +
                            (voteForm.conflict_votes.includes(c.match_name)
                              ? 'bg-orange-600 text-white'
                              : 'bg-gray-800 text-gray-400 hover:bg-gray-700')
                          }
                        >
                          CON
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 text-xs text-gray-500 font-mono">
                  <div className="grid grid-cols-5 gap-2">
                    <div>YES: {voteForm.yes_votes.length}</div>
                    <div>NO: {voteForm.no_votes.length}</div>
                    <div>ABS: {voteForm.abstained_votes.length}</div>
                    <div>N/P: {voteForm.not_present_votes.length}</div>
                    <div>CON: {voteForm.conflict_votes.length}</div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowVoteModal(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-sm uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-4 rounded-sm uppercase"
                >
                  Save Vote
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── WARD PULSE TAB ─────────────────────────────────────── */}
      {/* ─── NONPROFIT TAB ─────────────────────────────────────────── */}
      {activeTab === 'nonprofits' && (
        <NonprofitAdminTab />
      )}

      {/* ─── WARD PHOTOS TAB ────────────────────────────────────────── */}
      {activeTab === 'ward photos' && (
        <WardPhotosAdminTab />
      )}

      {/* ─── EVENTS TAB ────────────────────────────────────────────── */}
      {activeTab === 'events' && (
        <EventsTab />
      )}

      {/* ─── WARD PULSE TAB ─────────────────────────────────────────── */}
      {activeTab === 'ward pulse' && (
        <WardPulseAdmin api={api} toast={toast} />
      )}

      {/* ─── COUNCIL AUDITOR TAB ─────────────────────────────────── */}
      {activeTab === 'council auditor' && (
        <AuditorTab api={api} toast={toast} />
      )}

      {activeTab === 'alerts' && (
        <AlertsTab api={api} toast={toast} />
      )}

      {activeTab === 'push' && (
        <PushTab />
      )}

    </div>
  );
};

export default AdminPanel;
